import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { driverApi } from '../../services/driverApi';

interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    name: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  currentEsim: {
    item_code: string;
    due_date: string;
    renewal_count: number;
    iccid: string;
  };
  onSuccess: () => void;
}

interface TopUpPackage {
  id: string;
  name: string;
  description: string;
  data_mb: number;
  validity_days: number;
  price: number;
}

export const TopUpModal: React.FC<TopUpModalProps> = ({
  isOpen,
  onClose,
  user,
  currentEsim,
  onSuccess
}) => {
  const [packages, setPackages] = useState<TopUpPackage[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen && currentEsim.iccid) {
      loadTopupPackages();
    }
  }, [isOpen, currentEsim.iccid]);

  const loadTopupPackages = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await driverApi.getTopupPackages(currentEsim.iccid);
        if (response.success && response.packages) {
          setPackages(response.packages);
          if (response.packages.length > 0) {
            setSelectedPackage(response.packages[0].package_id);
          }
        } else {
          setError(response.error || 'Failed to load top-up packages');
        }
    } catch (err: any) {
      setError(err.message || 'Failed to load top-up packages');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPackage) return;

    setSubmitting(true);
    setError('');
    try {
      const response = await driverApi.topup(currentEsim.iccid, selectedPackage, 1);
      if (response.status === 'ok') {
        onSuccess();
        onClose();
      } else {
        setError('Top-up failed');
      }
    } catch (err: any) {
      setError(err.message || 'Top-up failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Manual Top-Up</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          <div className="mb-4">
            <div className="bg-blue-50 p-4 rounded-md mb-4">
              <h3 className="font-medium text-blue-800 mb-1">User Details</h3>
              <p className="text-blue-700">
                {user.first_name} {user.last_name} ({user.email})
              </p>
            </div>
            
            <div className="bg-gray-100 p-4 rounded-md mb-4">
              <h3 className="font-medium text-gray-700 mb-1">Current eSIM</h3>
              <p className="text-gray-800">{currentEsim.item_code}</p>
              <p className="text-gray-600 text-sm">Due: {currentEsim.due_date || '-'}</p>
              <p className="text-gray-600 text-sm">Renewals: {currentEsim.renewal_count || 0}</p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="package" className="block text-sm font-medium text-gray-700 mb-1">
                Top-Up Plan
              </label>
              {loading ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  <span className="text-gray-500">Loading packages...</span>
                </div>
              ) : packages.length > 0 ? (
                <select
                  id="package"
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                        {packages.map((pkg, index) => (
                          <option key={pkg.package_id || index} value={pkg.package_id}>
                            {pkg.name} - €{pkg.gross_price_eur}
                          </option>
                        ))}
                </select>
              ) : (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  <span className="text-gray-500">No packages available</span>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {packages.length > 0 ? 'Select a top-up plan' : 'No top-up packages available for this eSIM'}
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedPackage || submitting || packages.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {submitting ? 'Processing...' : 'Confirm Top-Up'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
